import axios from "axios";
import { global, useMenuStore } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import { fnGetTableById } from "@shared/api/restApi";

/**
 * 드릴다운 공통 유틸 (닷넷 FN_DriillDown + AP_SYS_DRILLDOWN_01)
 * FE는 호출 컨텍스트(대상 메뉴, 문서번호, 라인)만 넘기고, 등록/조회 판별은 백엔드에서 실행
 */

export interface DrilldownParams {
  cd_menu: string; // 대상 메뉴 코드
  no_docu: string; // 문서번호
  no_line?: string; // 라인번호 목록 ('1|2|3|' 형태)
  /** 메뉴별 추가 파라미터(예: TRA의 fg_map). 그대로 백엔드로 전달되어 판별 쿼리에서 #{key}로 쓰고, 조회 판별 결과에도 전달된다. */
  [key: string]: any;
}

export interface DrilldownResult {
  fg: "Registration" | "Search";
  cd_menu: string;
  cd_firm: string;
  no_docu: string;
  no_line?: string;
  cd_biz?: string;
  nm_biz?: string;
  fg_trns?: string;
  fg_req?: string;
  fg_prq?: string;
  cd_io?: string;
  /* 조회 판별 시 */
  no_io_chase?: string;
  dt_from?: string;
  dt_to?: string;
  cd_gp?: string;
  cd_emp?: string;
  cd_cust?: string;
  cd_rout?: string;
  fg_wo?: string;
  st_wo?: string;
  tp_rout?: string;
  [key: string]: any;
}

/**
 * 수불번호의 거래구분(mtl_qtioh.fg_trns).
 * 거래구분이 조회 필수인 화면(구매입고·출고)은 드릴다운 시 이 값을 넣지 않으면 기본값(국내)으로 조회돼 0건이 된다.
 */
export const fnGetIoFgTrns = async (
  no_io?: string
): Promise<string | undefined> => {
  if (!no_io) return undefined;
  const rows: any = await fnGetTableById({
    id: "drilldown_io_fg_trns",
    params: { no_io },
  });
  return (Array.isArray(rows) && rows[0]?.fg_trns) || undefined;
};

/** 드릴다운 판별 호출 */
export const fnResolveDrilldown = async (
  params: DrilldownParams
): Promise<DrilldownResult | undefined> => {
  const response = await axios.post("/snotes/drilldown/resolve", params, {
    headers: {
      "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
    },
  });
  return response.data;
};

/**
 * 드릴다운 실행: 판별 후 대상 메뉴를 열고 onActivate 호출.
 * @returns 대상 메뉴를 열었으면 true
 */
export const fnDrilldown = async (
  params: DrilldownParams
): Promise<boolean> => {
  const result = await fnResolveDrilldown(params);
  if (!result || !result.fg) {
    await showDialog("alter", "대상을 판별할 수 없습니다.");
    return false;
  }

  const menuItems = useMenuStore.getState().menuItems;
  const target = menuItems
    ? menuItems.find((item: any) => item?.cd_menu === result.cd_menu)
    : undefined;
  if (!target) {
    await showDialog("alter", "대상 메뉴를 찾을 수 없습니다.");
    return false;
  }

  const filePath =
    `./${target.cd_module}/${target.cd_menu}/${target.cd_menu}`.toUpperCase();

  const nodeData = { ...result };

  (window as any).onActivate(
    target.cd_menu,
    target.nm_menu,
    target.cd_module,
    filePath,
    target,
    target.nm_path,
    nodeData,
    `fg_drilldown.${result.fg}`
  );
  return true;
};
